import React, { useEffect, useState } from "react";
import { Column, LinkedBoard } from "./table.data.interface";
import { DropdownChevronDown } from "@vibe/icons";
import { IconButton, Loader } from "@vibe/core";
import { Retry } from "@vibe/icons";
import Collapse from "@mui/material/Collapse";
import UnsharedColumnRow from "./UnsharedColumnRow";

interface OtherBoardsColumnsProps {
  data: LinkedBoard[];
  /** Unshared columns per board (from Monday + filtered by sharedColumns in parent) */
  unsharedColumnsByBoard?: Record<string, Column[]>;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const OtherBoardsColumns = ({
  data,
  unsharedColumnsByBoard = {},
  isLoading = false,
  error = null,
  onRetry,
}: OtherBoardsColumnsProps) => {
  const [retryLoading, setRetryLoading] = useState(false);

  const getUnsharedForBoard = (item: LinkedBoard): Column[] => {
    return unsharedColumnsByBoard[item.boardId] ?? [];
  };

  const [expandedBoardIds, setExpandedBoardIds] = useState<Set<string>>(
    () => new Set(data.map((b) => b.boardId)),
  );

  useEffect(() => {
    setExpandedBoardIds((prev) => {
      const next = new Set(prev);
      data.forEach((b) => next.add(b.boardId));
      return next;
    });
  }, [data]);

  const toggleBoard = (boardId: string) => {
    setExpandedBoardIds((prev) => {
      const next = new Set(prev);
      if (next.has(boardId)) next.delete(boardId);
      else next.add(boardId);
      return next;
    });
  };

  if (!data.length) return null;

  if (error) {
    return (
      <div className="flex flex-col gap-2 justify-center items-center py-6">
        {retryLoading ? (
          <Loader size="small" color="primary" />
        ) : (
          <>
            <IconButton
              onClick={async () => {
                if (!onRetry) return;
                setRetryLoading(true);
                try {
                  await onRetry();
                } finally {
                  setRetryLoading(false);
                }
              }}
              className="cursor-pointer"
              icon={Retry}
              size="small"
              kind="primary"
            />
            <span className="!font-figtree text-sm text-gray-600">
              Could not load columns for other boards. Try again.
            </span>
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex gap-2 items-center py-4 my-4">
        <Loader size="small" color="primary" />
        <span className="!font-figtree text-sm text-gray-500">
          Loading columns for other boards…
        </span>
      </div>
    );
  }

  return (
    <div>
      {data.map((item: LinkedBoard) => {
        const unsharedColumns = getUnsharedForBoard(item);
        const isExpanded = expandedBoardIds.has(item.boardId);
        // Monday gives no name for a board this user cannot open, which usually means it is private.
        const isPrivateBoard = !item.boardName;

        return (
          <div
            key={item.boardId}
            className="overflow-hidden border border-t border-b-0 border-gray-200 border-solid last:border-b last:border-b- last:mb-4 first:mt-4 border-x-0"
          >
            {/* Board Header */}
            <div
              onClick={() => toggleBoard(item.boardId)}
              className="flex gap-2 items-center p-4 pl-0 w-full text-left transition-colors cursor-pointer hover:bg-gray-50"
              aria-expanded={isExpanded}
            >
              <span
                className="flex transition-transform shrink-0"
                style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)" }}
              >
                <DropdownChevronDown size={20} />
              </span>
              <h3 className="!font-figtree font-medium flex-1">
                {isPrivateBoard ? "Private Board" : item.boardName}
              </h3>
              {unsharedColumns.length > 0 && (
                <span className="text-sm text-gray-500">
                  {unsharedColumns.length} column{unsharedColumns.length !== 1 ? "s" : ""} not
                  shared
                </span>
              )}
            </div>

            {/* Unshared Columns - same UI as ItemViewColumnValues */}
            <Collapse in={isExpanded} timeout={200} unmountOnExit>
              <div className="max-w-[640px] mx-auto">
                {isPrivateBoard ? (
                  <p className="!font-figtree text-sm text-gray-500 p-2 pl-6">
                    This may be a private board you don&apos;t have access to, so its columns
                    can&apos;t be shown.
                  </p>
                ) : unsharedColumns.length === 0 ? (
                  <p className="!font-figtree text-sm text-gray-500 p-2 pl-6">
                    All columns are shared with this board.
                  </p>
                ) : (
                  <div className="grid grid-cols-item-view-values-cols gap-x-[2px] pb-4 pl-4">
                    {unsharedColumns.map((column) => (
                      <UnsharedColumnRow
                        key={column.columnId}
                        column={column}
                        itemId={item.itemId}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Collapse>
          </div>
        );
      })}
    </div>
  );
};

export default OtherBoardsColumns;
